import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import CreateModuleModal from '../../components/admin/CreateModuleModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { Plus, Edit, Trash2, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../hooks/useConfirm';

export default function AdminModulesPage() {
  const toast = useToast();
  const confirmDialog = useConfirm();
  const [modules, setModules] = useState([]);
  const [modulesWithAccess, setModulesWithAccess] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadModules();
  }, []);

  async function loadModules() {
    try {
      const { data } = await api.get('/modules');
      setModules(data);
      
      // Carregar informações de acesso para cada módulo
      const modulesWithAccessInfo = await Promise.all(
        data.map(async (module) => {
          try {
            const { data: accessData } = await api.get(`/modules/${module.id}/access-info`);
            return {
              ...module,
              studentsCount: accessData?.studentsCount || 0,
              students: accessData?.students || []
            };
          } catch (error) {
            console.error(`Erro ao carregar acesso do módulo ${module.id}:`, error);
            return {
              ...module,
              studentsCount: 0,
              students: []
            };
          }
        })
      );
      
      setModulesWithAccess(modulesWithAccessInfo);
    } catch (error) {
      console.error('Erro ao carregar módulos:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleModuleCreated(newModule) {
    // Recarregar a lista completa para garantir que está atualizada
    loadModules();
  }

  async function handleDeleteModule(moduleId, moduleName) {
    const confirmed = await confirmDialog.confirm({
      title: 'Excluir Módulo',
      message: `Deseja realmente excluir o módulo "${moduleName}"?\n\nEsta ação não pode ser desfeita e todos os vídeos do módulo também serão excluídos.`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      console.log('🗑️ Excluindo módulo:', moduleId);
      await api.delete(`/modules/${moduleId}`);
      console.log('✅ Módulo excluído com sucesso');
      
      // Remover de ambas as listas
      setModules(modules.filter(m => m.id !== moduleId));
      setModulesWithAccess(modulesWithAccess.filter(m => m.id !== moduleId));
      
      toast.success('Módulo excluído com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao excluir módulo:', error);
      toast.error(error.response?.data?.error || 'Erro ao excluir módulo');
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-12">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-text-primary mb-3">Módulos Disponíveis</h1>
          <p className="text-gray-600 dark:text-text-secondary text-lg">Gerencie seus cursos e veja quem tem acesso</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-2xl flex items-center gap-2 transition shadow-lg shadow-primary-500/20"
        >
          <Plus size={20} /> Novo Módulo
        </button>
      </div>

      {modules.length === 0 ? (
        <div className="text-center py-20 bg-background-800 dark:bg-background-800 border border-gray-700 dark:border-gray-700 rounded-3xl">
          <div className="mb-6">
            <svg className="w-24 h-24 mx-auto text-gray-600 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-text-primary mb-2">
            Nenhum módulo criado ainda
          </h3>
          <p className="text-text-secondary">
            Comece criando seu primeiro módulo
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {modulesWithAccess.map((module) => (
            <motion.div
              key={module.id}
              whileHover={{ y: -6 }}
              className="bg-background-800 dark:bg-background-800 border border-gray-700 dark:border-gray-700 rounded-3xl overflow-hidden group"
            >
              <Link to={`/admin/modules/${module.id}`}>
                <div className="relative">
                  {module.thumbnail_url ? (
                    <img src={module.thumbnail_url} alt={module.title_pt} className="w-full h-48 object-cover" />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-background-700 dark:from-background-700 to-background-900 dark:to-background-900 flex items-center justify-center">
                      <svg className="w-20 h-20 text-gray-700 dark:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                      </svg>
                    </div>
                  )}
                  
                  {/* Badge de status */}
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm ${
                      module.is_published
                        ? 'bg-success/20 text-success border border-success/50'
                        : 'bg-gray-700/50 text-text-secondary border border-gray-600'
                    }`}>
                      {module.is_published ? 'Publicado' : 'Rascunho'}
                    </span>
                  </div>
                </div>
              </Link>

              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-text-primary group-hover:text-primary-500 transition-colors mb-2">
                  {module.title_pt}
                </h3>
                <p className="text-gray-600 dark:text-text-secondary text-sm line-clamp-2 mb-4">
                  {module.description_pt}
                </p>
                
                {/* Info de Acesso */}
                <div className="mb-4 p-3 bg-gray-100 dark:bg-background-900 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-sm">
                    <Users size={16} className="text-primary-500" />
                    <span className="font-semibold text-gray-900 dark:text-text-primary">
                      {module.studentsCount} {module.studentsCount === 1 ? 'usuário' : 'usuários'}
                    </span>
                    <span className="text-gray-500 dark:text-gray-500">com acesso</span>
                  </div>
                  {module.students && module.students.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {module.students.slice(0, 3).map((student) => (
                        <span
                          key={student.id}
                          className="text-xs px-2 py-1 bg-primary-500/10 text-primary-500 rounded-full"
                          title={student.full_name}
                        >
                          {student.full_name.split(' ')[0]}
                        </span>
                      ))}
                      {module.students.length > 3 && (
                        <span className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                          +{module.students.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <Link
                    to={`/admin/modules/${module.id}`}
                    className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 py-3 rounded-2xl flex items-center justify-center gap-2 text-gray-900 dark:text-text-primary transition font-medium"
                  >
                    <Edit size={18} /> Editar
                  </Link>
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      handleDeleteModule(module.id, module.title_pt);
                    }}
                    className="flex-1 bg-red-500/10 text-red-400 hover:bg-red-500/20 py-3 rounded-2xl flex items-center justify-center gap-2 transition font-medium"
                  >
                    <Trash2 size={18} /> Excluir
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal de criar módulo */}
      <CreateModuleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModuleCreated}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.handleCancel}
        onConfirm={confirmDialog.handleConfirm}
        {...confirmDialog.config}
      />
    </div>
  );
}
